import React, { useState } from 'react';
import { 
  Brain, Lightbulb, Link, Package,
  TrendingUp, Shield, CheckCircle, AlertCircle
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface ShelfItem {
  skuId: string;
  skuName: string;
  category: string;
  position: { shelf: number; row: number; column: number };
  facings: number;
  salesPerDay: number;
  profitMargin: number;
  turnoverRate: number;
  status: 'optimal' | 'underperforming' | 'overperforming' | 'dead';
}

interface ExtractedRule {
  id: string;
  type: 'placement' | 'grouping' | 'facing' | 'zone' | 'brand';
  name: string;
  description: string;
  confidence: number;
  frequency: number;
  impact: 'high' | 'medium' | 'low';
  examples: string[];
  validation: 'verified' | 'testing' | 'new';
}

interface RuleExtractorProps {
  shelfItems: ShelfItem[];
}

const RuleExtractor: React.FC<RuleExtractorProps> = ({ shelfItems }) => {
  const [isExtracting, setIsExtracting] = useState(false);
  const [selectedRuleType, setSelectedRuleType] = useState<string>('all');
  
  // 抽出された陳列ルール（モック）
  const extractedRules: ExtractedRule[] = [
    {
      id: 'RULE001',
      type: 'placement',
      name: '売れ筋商品のゴールデンゾーン配置',
      description: '月間売上TOP20の商品は必ずアイレベル（棚2-3段目）に配置されている',
      confidence: 92,
      frequency: 45,
      impact: 'high',
      examples: ['コカ・コーラ500ml', 'ポテトチップスうすしお', '牛乳1L'],
      validation: 'verified'
    },
    {
      id: 'RULE002',
      type: 'grouping',
      name: 'ブランド内カテゴリグループ化',
      description: '同一ブランドの商品は隣接して配置し、サイズ順に並べる',
      confidence: 88,
      frequency: 38,
      impact: 'medium',
      examples: ['コカ・コーラ製品群', 'カルビー製品群', 'ネスレ製品群'],
      validation: 'verified'
    },
    {
      id: 'RULE003',
      type: 'facing',
      name: '売上連動フェーシング',
      description: '日販30個以上の商品は最低2フェース、50個以上は3フェース確保',
      confidence: 85,
      frequency: 52,
      impact: 'high',
      examples: ['ペットボトル飲料', 'パン類', 'カップ麺'],
      validation: 'testing'
    },
    {
      id: 'RULE004',
      type: 'zone',
      name: '重量商品の下段配置',
      description: '2kg以上の商品は必ず棚4-5段目に配置し、安全性を確保',
      confidence: 95,
      frequency: 28,
      impact: 'medium',
      examples: ['2Lペットボトル', '米', '調味料大容量'],
      validation: 'verified'
    },
    {
      id: 'RULE005',
      type: 'grouping',
      name: 'クロスMD隣接配置',
      description: '関連購買率30%以上の商品ペアは隣接配置で相乗効果',
      confidence: 78,
      frequency: 22,
      impact: 'high',
      examples: ['ビール・おつまみ', 'パン・ジャム', 'コーヒー・ミルク'],
      validation: 'new'
    },
    {
      id: 'RULE006',
      type: 'brand',
      name: 'PB商品の優先表示',
      description: 'プライベートブランド商品は各カテゴリで最も視認性の高い位置に配置',
      confidence: 82,
      frequency: 35,
      impact: 'high',
      examples: ['トップバリュ', 'セブンプレミアム', 'みなさまのお墨付き'],
      validation: 'verified'
    },
    {
      id: 'RULE007',
      type: 'placement',
      name: '新商品の特設配置',
      description: '発売1ヶ月以内の新商品はエンド台または特設コーナーで訴求',
      confidence: 75,
      frequency: 18,
      impact: 'medium',
      examples: ['季節限定商品', '新発売飲料', '期間限定菓子'],
      validation: 'testing'
    },
    {
      id: 'RULE008',
      type: 'zone',
      name: '価格帯別の縦陳列',
      description: '同カテゴリ内で価格帯別に縦に配置（上から高→低）',
      confidence: 70,
      frequency: 40,
      impact: 'low',
      examples: ['ワイン', 'チョコレート', 'インスタント麺'],
      validation: 'new'
    }
  ];
  
  const handleExtractRules = () => {
    setIsExtracting(true);
    setTimeout(() => {
      setIsExtracting(false);
    }, 2000);
  };
  
  const getRuleIcon = (type: string) => {
    switch (type) {
      case 'placement': return <Package size={16} />;
      case 'grouping': return <Link size={16} />;
      case 'facing': return <TrendingUp size={16} />;
      case 'zone': return <Shield size={16} />;
      case 'brand': return <Lightbulb size={16} />;
      default: return <Brain size={16} />;
    }
  };
  
  const ruleTypes = ['all', 'placement', 'grouping', 'facing', 'zone', 'brand'];
  const filteredRules = selectedRuleType === 'all' 
    ? extractedRules 
    : extractedRules.filter(rule => rule.type === selectedRuleType);

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
            陳列ルール学習・抽出
          </h3>
          <p className="text-sm mt-1" style={{ color: colors.text.secondary }}>
            過去の優れた棚割りパターンから暗黙的なルールをAIが学習
          </p>
        </div>
        <Button 
          variant="primary"
          onClick={handleExtractRules}
          disabled={isExtracting}
        >
          {isExtracting ? (
            <>
              <Brain size={16} className="animate-pulse" />
              学習中...
            </>
          ) : (
            <>
              <Brain size={16} />
              ルール抽出
            </>
          )}
        </Button>
      </div>
      
      {/* 統計サマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Brain size={20} style={{ color: colors.accent.purple }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {extractedRules.length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                抽出ルール
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} style={{ color: colors.support.success }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {extractedRules.filter(r => r.validation === 'verified').length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                検証済み
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <TrendingUp size={20} style={{ color: colors.accent.blue }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {extractedRules.filter(r => r.impact === 'high').length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                高影響度
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Lightbulb size={20} style={{ color: colors.status.warning }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {extractedRules.filter(r => r.validation === 'new').length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                新規発見
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* フィルター */}
      <div className="flex items-center gap-2">
        {ruleTypes.map(type => (
          <Button
            key={type}
            variant={selectedRuleType === type ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSelectedRuleType(type)}
          >
            {type === 'all' ? '全て' : 
             type === 'placement' ? '配置' :
             type === 'grouping' ? 'グループ' :
             type === 'facing' ? 'フェース' :
             type === 'zone' ? 'ゾーン' : 'ブランド'}
          </Button>
        ))}
      </div>
      
      {/* ルール一覧 */}
      <div className="grid grid-cols-2 gap-4">
        {filteredRules.map(rule => (
          <Card key={rule.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getRuleIcon(rule.type)}
                <h4 className="font-medium" style={{ color: colors.text.primary }}>
                  {rule.name}
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={rule.validation === 'verified' ? 'success' : 
                          rule.validation === 'testing' ? 'warning' : 'secondary'}
                  size="small"
                >
                  {rule.validation === 'verified' ? '検証済' : 
                   rule.validation === 'testing' ? 'テスト中' : '新規'}
                </Badge>
                <Badge 
                  variant={rule.impact === 'high' ? 'error' : 
                          rule.impact === 'medium' ? 'warning' : 'secondary'}
                  size="small"
                >
                  {rule.impact === 'high' ? '高' : 
                   rule.impact === 'medium' ? '中' : '低'}影響
                </Badge>
              </div>
            </div>
            
            <p className="text-sm mb-3" style={{ color: colors.text.secondary }}>
              {rule.description}
            </p>
            
            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: colors.text.secondary }}>信頼度</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all"
                      style={{ 
                        width: `${rule.confidence}%`,
                        backgroundColor: rule.confidence > 85 ? colors.support.success : 
                                       rule.confidence > 70 ? colors.status.warning : 
                                       colors.status.error
                      }}
                    />
                  </div>
                  <span className="font-medium">{rule.confidence}%</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: colors.text.secondary }}>出現頻度</span>
                <span className="font-medium">{rule.frequency}回/月</span>
              </div>
            </div>
            
            <div className="pt-3 border-t" style={{ borderColor: colors.ui.border }}>
              <div className="text-xs font-medium mb-2" style={{ color: colors.text.primary }}>
                適用例:
              </div>
              <div className="flex flex-wrap gap-1">
                {rule.examples.map((example, idx) => (
                  <Badge key={idx} variant="secondary" size="small">
                    {example}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="mt-3 flex gap-2">
              <Button size="small" variant="secondary">
                詳細
              </Button>
              <Button size="small" variant="primary">
                適用
              </Button>
            </div>
          </Card>
        ))}
      </div>
      
      {/* ルール統合提案 */}
      <Card className="p-4 border-l-4" style={{ borderLeftColor: colors.accent.purple }}>
        <div className="flex items-start gap-3">
          <Lightbulb size={20} style={{ color: colors.accent.purple }} />
          <div className="flex-1">
            <h4 className="font-semibold mb-2" style={{ color: colors.text.primary }}>
              AI推奨: 新しい統合ルール
            </h4>
            <p className="text-sm mb-3" style={{ color: colors.text.secondary }}>
              複数の既存ルールを組み合わせることで、さらに効果的な陳列パターンを発見しました。
            </p>
            <div className="p-3 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <div className="font-medium mb-1">「季節商品×売れ筋の黄金配置」</div>
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                季節商品の中で売上TOP5は必ずゴールデンゾーンに3フェース以上で配置し、
                関連商品を隣接させることで、平均売上が23%向上する可能性があります。
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" size="small">
                シミュレーション
              </Button>
              <Button variant="primary" size="small">
                テスト導入
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RuleExtractor;
